import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import MultiImageUploader from "@/components/MultiImageUploader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { value: "tshirt", label: "T-Shirt" },
  { value: "poster", label: "Poster" },
  { value: "collectible", label: "Collectible" },
  { value: "action_figure", label: "Action Figure" },
  { value: "membership", label: "Membership" },
];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "tshirt",
    universe_id: "",
    image: "",
    images: [],
    stock: "10",
    featured: false,
    sizes: "",
    duration_months: "",
  });

  const fetchData = async () => {
    try {
      const [prodRes, uniRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/universes`),
      ]);
      setProducts(prodRes.data);
      setUniverses(uniRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
      universe_id: formData.universe_id || null,
      images: formData.images || [],
      sizes: formData.sizes ? formData.sizes.split(",").map(s => s.trim()).filter(Boolean) : [],
      duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, submitData);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, submitData);
        toast.success("Product created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      universe_id: product.universe_id || "",
      image: product.image || "",
      images: product.images || [],
      stock: product.stock.toString(),
      featured: product.featured,
      sizes: product.sizes?.join(", ") || "",
      duration_months: product.duration_months?.toString() || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      price: "",
      category: "tshirt",
      universe_id: "",
      image: "",
      images: [],
      stock: "10",
      featured: false,
      sizes: "",
      duration_months: "",
    });
  };

  const getUniverseName = (id) => {
    const uni = universes.find(u => u.id === id);
    return uni ? uni.name : "-";
  };

  return (
    <AdminLayout title="Manage Products">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{products.length} products</p>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Universe</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Featured</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        src={product.image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100"}
                        alt={product.name}
                        className="w-10 h-10 object-cover"
                      />
                      <span className="font-medium text-white truncate max-w-[200px]">{product.name}</span>
                    </div>
                  </td>
                  <td className="text-slate-400 capitalize">{product.category.replace("_", " ")}</td>
                  <td className="text-slate-400">{getUniverseName(product.universe_id)}</td>
                  <td className="text-white font-mono">R{product.price.toFixed(2)}</td>
                  <td className={product.stock === 0 ? "text-red-500" : "text-slate-400"}>{product.stock}</td>
                  <td>{product.featured ? <span className="text-yellow-500">Yes</span> : <span className="text-slate-500">No</span>}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        className="hover:text-red-500"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
            <div>
              <Label className="text-slate-300">Product Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-dark mt-1"
                required
                data-testid="product-name"
              />
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-dark mt-1 min-h-[80px]"
                required
                data-testid="product-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Price (ZAR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="input-dark mt-1"
                  required
                  data-testid="product-price"
                />
              </div>
              <div>
                <Label className="text-slate-300">Stock</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  className="input-dark mt-1"
                  required
                  data-testid="product-stock"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="product-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Universe (optional)</Label>
                <Select
                  value={formData.universe_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, universe_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="product-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    <SelectItem value="none">None</SelectItem>
                    {universes.map((uni) => (
                      <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Main Image URL (or upload below)</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="input-dark mt-1"
                placeholder="https://..."
                data-testid="product-image"
              />
            </div>

            <div>
              <Label className="text-slate-300">Product Images</Label>
              <div className="mt-1">
                <MultiImageUploader
                  images={formData.images}
                  onChange={(imgs) => setFormData({ ...formData, images: imgs, image: imgs[0] || formData.image })}
                  maxImages={8}
                />
              </div>
            </div>

            {formData.category === "tshirt" && (
              <div>
                <Label className="text-slate-300">Sizes (comma-separated)</Label>
                <Input
                  value={formData.sizes}
                  onChange={(e) => setFormData({ ...formData, sizes: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="S, M, L, XL, XXL"
                  data-testid="product-sizes"
                />
              </div>
            )}

            {formData.category === "membership" && (
              <div>
                <Label className="text-slate-300">Duration (months, 0 for lifetime)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.duration_months}
                  onChange={(e) => setFormData({ ...formData, duration_months: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="12"
                  data-testid="product-duration"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                data-testid="product-featured"
              />
              <Label htmlFor="featured" className="text-slate-300 cursor-pointer">Featured product</Label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="product-submit">
                {editingProduct ? "Update" : "Create"} Product
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProducts;
